import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ReviewRecord, HACCPTableName } from '../../types/adminReview';
import { adminReviewApiService } from '../../services/adminReviewApiService';

interface AdminReviewState {
  readyForVerification: ReviewRecord[];
  incompleteRecords: ReviewRecord[];
  loading: boolean;
  error: string | null;
  verifying: Record<string, boolean>; // Track verification in progress by record id
}

const initialState: AdminReviewState = {
  readyForVerification: [],
  incompleteRecords: [],
  loading: false,
  error: null,
  verifying: {}
};

/**
 * Fetch all incomplete HACCP records
 */
export const fetchIncompleteRecords = createAsyncThunk(
  'adminReview/fetchIncompleteRecords',
  async () => {
    const response = await adminReviewApiService.fetchIncompleteRecords();
    return {
      readyForVerification: response.readyForVerification,
      incompleteRecords: response.incompleteRecords
    };
  }
);

/**
 * Verify a record (update verified_by field)
 */
export const verifyRecordAsync = createAsyncThunk(
  'adminReview/verifyRecord',
  async ({ table, id, verifiedBy }: { table: HACCPTableName; id: string; verifiedBy: string }) => {
    await adminReviewApiService.verifyRecord(table, id, verifiedBy);
    return { table, id };
  }
);

const adminReviewSlice = createSlice({
  name: 'adminReview',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch incomplete records
    builder
      .addCase(fetchIncompleteRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncompleteRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.readyForVerification = action.payload.readyForVerification;
        state.incompleteRecords = action.payload.incompleteRecords;
      })
      .addCase(fetchIncompleteRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch incomplete records';
      });

    // Verify record
    builder
      .addCase(verifyRecordAsync.pending, (state, action) => {
        const { id } = action.meta.arg;
        state.verifying[id] = true;
      })
      .addCase(verifyRecordAsync.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.verifying[id] = false;
        // Remove the verified record from readyForVerification list
        state.readyForVerification = state.readyForVerification.filter(
          record => record.record.id !== id
        );
      })
      .addCase(verifyRecordAsync.rejected, (state, action) => {
        const { id } = action.meta.arg;
        state.verifying[id] = false;
        state.error = action.error.message || 'Failed to verify record';
      });
  }
});

export const { clearError } = adminReviewSlice.actions;
export default adminReviewSlice.reducer;
